// Unpublished Work © 2022-2026 Deere & Company.

import PropTypes from 'Utils/prop-type-utils';
import {connect} from 'react-redux';
import React from 'react';
import {deleteJobTemplateById, getJobTemplates} from 'Services/job-service';
import SwitchInput from 'Ui/components/common/form/switch-input';
import {fetchEffectData, useDeepEffect, useDeepMemo} from 'Utils/react-utils';
import {formatMinutesToHours} from 'Utils/time-utils';
import {alphaNumericCompare} from 'Ui/models/maintenance';
import {TOAST_TYPE} from 'Src/ui-core/constants/toast-constants';
import {useToast} from 'Src/ui-core/react-hooks/use-toast';
import {closeDialog, openDialog} from 'Store/actions/dialogs';
import dialogTypes from 'Ui/components/common/dialog-types';
import {replaceTranslationNames} from 'Utils/translation-utils';
import AddButton from 'Ui/components/settings/common/add-button';
import {Button} from '@deere/isg.component-library';
import {IconWdt} from '@deere/icons';
import EditLink from 'Ui/components/common/data-table/edit-link';
import {createPpeIconMap} from 'Utils/icon-utils';
import {createCSV, downloadCSV} from '../common/export-csv';
import {CSV_JOBS_FILENAME, CSV_JOBS_HEADERS} from '../constants/settings';
import moment from 'moment';
import DataGridWrapper from 'Src/ui-core/components/common/data-table/data-grid-wrapper';

const ICON_STYLE = {
    style: {
        height: '20px',
        width: '20px',
        marginRight: '5px'
    }
};

const PPE_ICON_MAP = createPpeIconMap(ICON_STYLE);

function getDataGridColumns(translations, openJobDialog, fetchJobTemplates) {
    return [
        {
            headerName: translations.NAME,
            field: 'title',
            width: 175,
            renderCell: (params) => {
                return (
                    <EditLink
                        onClick={() =>
                            openJobDialog({
                                job: params.row,
                                getUpdatedJobTemplates: async () => {
                                    await fetchJobTemplates();
                                }
                            })
                        }
                    >
                        {params.value}
                    </EditLink>
                );
            }
        },
        {
            headerName: translations.ONLINK_SECONDARY_NAME,
            sortComparator: alphaNumericCompare,
            field: 'secondaryName',
            width: 175
        },
        {
            headerName: translations.PRODUCT_TYPE,
            width: 120,
            field: 'jobCategory'
        },
        {
            headerName: translations.AREA,
            width: 120,
            field: 'equipmentArea'
        },
        {
            headerName: translations.ONLINK_PPE,
            field: 'ppeListIcons',
            sortable: false,
            width: 100
        },
        {
            headerName: translations.ONLINK_EST_HRS,
            field: 'estDuration',
            type: 'number',
            width: 120
        },
        {
            headerName: translations.STATUS,
            field: 'status',
            width: 100
        }
    ];
}

function getFilterComponent(
    translations,
    showInactive,
    setShowInactive,
    openJobDialog,
    fetchJobTemplates,
    membership,
    jobs,
    exportFn
) {
    return (
        <div className='extra-table-options'>
            <SwitchInput
                checked={showInactive}
                name='showInactive'
                offLabel={translations.ONLINK_SHOW_INACTIVE}
                onChange={() => setShowInactive(!showInactive)}
                onLabel={translations.ONLINK_HIDE_INACTIVE}
                translations={translations}
            />
            <AddButton
                className='add-job-button'
                label={translations.ONLINK_ADD_JOB}
                onClick={() => {
                    openJobDialog({
                        getUpdatedJobTemplates: async () => {
                            await fetchJobTemplates();
                        }
                    });
                }}
            />
            <Button
                onClick={() => exportFn(membership, jobs)}
                startIcon={<IconWdt/>}
                sx={{
                    marginLeft: '0.5rem'
                }}
            >
                {translations.EXPORT}
            </Button>
        </div>
    );
}

function exportJobs(membership, jobs) {
    const {name} = membership;
    const today = moment().format('MM_DD-YYYY');
    const csv = createCSV(jobs, CSV_JOBS_HEADERS);

    downloadCSV(csv, `${CSV_JOBS_FILENAME}-${name}-${today}.csv`);
}

function JobsSettings(props) {
    const {
        closeConfirmation,
        membership,
        openConfirmation,
        openJobDialog,
        translations
    } = props;

    const {addToast} = useToast();
    const [loading, setLoading] = React.useState(false);
    const [showInactive, setShowInactive] = React.useState(false);
    const [jobs, setJobs] = React.useState([]);

    async function fetchJobTemplates(isMounted = () => true) {
        setLoading(true);

        const {templates} = await getJobTemplates();

        if (isMounted()) {
            const formattedJobs = templates.map((jobTemplate) => {
                return {
                    ...jobTemplate,
                    estDuration:
                        jobTemplate.estDuration &&
                            formatMinutesToHours(jobTemplate.estDuration) ||
                        '',
                    ppeListIcons: jobTemplate.ppeList?.map(
                        (ppeName) => PPE_ICON_MAP[ppeName]
                    ),
                    status: jobTemplate.inactive ?
                        translations.inactive :
                        translations['EDIT_PDA_PAGE.LABEL.ACTIVE']
                };
            });

            setJobs(formattedJobs);
            setLoading(false);
        }
    }

    function deleteJob(jobTemplate) {
        openConfirmation({
            message: replaceTranslationNames(
                translations.ONLINK_ITEM_THIS_CANNOT_BE_UNDONE,
                {
                    '0': translations.jobs_job.toLowerCase()
                }
            ),
            title: replaceTranslationNames(translations.ONLINK_DELETE_ITEM, {
                '0': translations.jobs_job
            }),
            async onContinue() {
                closeConfirmation();

                try {
                    await deleteJobTemplateById(jobTemplate.jobTemplateId);

                    await fetchJobTemplates();
                } catch {
                    addToast({
                        message: replaceTranslationNames(
                            translations.ONLINK_CONFIRM_ITEM_DELETION_FAILED,
                            {
                                '0': translations.jobs_job.toLowerCase()
                            }
                        ),
                        type: TOAST_TYPE.ERROR
                    });
                }
            },
            onCancel: closeConfirmation
        });
    }

    useDeepEffect(() => fetchEffectData(fetchJobTemplates), []);

    const filteredJobsByStatus = useDeepMemo(
        () => showInactive ? jobs : jobs.filter((job) => !job.inactive),
        [showInactive, jobs]
    );

    return (
        <div className={'settings-container data-grid'}>
            <DataGridWrapper
                columns={getDataGridColumns(translations, openJobDialog, fetchJobTemplates)}
                deleteHandler={deleteJob}
                filterComponent={getFilterComponent(
                    translations,
                    showInactive,
                    setShowInactive,
                    openJobDialog,
                    fetchJobTemplates,
                    membership,
                    jobs,
                    exportJobs
                )}
                getRowId={(row) => row.jobTemplateId}
                initialState={{
                    sorting: {
                        sortModel: [
                            {
                                field: 'title',
                                sort: 'asc'
                            }
                        ]
                    }
                }}
                loading={loading}
                rows={filteredJobsByStatus}
                searchable={true}
                showDeleteColumn={true}
                slotProps={{
                    root: {
                        disableColumnMenu: true
                    }
                }}
                translations={translations}
            />
        </div>
    );
}

JobsSettings.propTypes = {
    closeConfirmation: PropTypes.func,
    membership: PropTypes.membership,
    openConfirmation: PropTypes.func,
    openJobDialog: PropTypes.func,
    translations: PropTypes.translations
};

export function mapStateToProps(state) {
    return {
        membership: state.membership,
        translations: state.translations
    };
}

export function mapDispatchToProps(dispatch) {
    return {
        closeConfirmation() {
            dispatch(closeDialog(dialogTypes.CONFIRMATION_DIALOG));
        },
        openConfirmation(props) {
            dispatch(openDialog(dialogTypes.CONFIRMATION_DIALOG, props));
        },
        openJobDialog(props) {
            dispatch(openDialog(dialogTypes.JOB_DIALOG, props));
        }
    };
}

export default connect(mapStateToProps, mapDispatchToProps)(JobsSettings);
